import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Users,
} from 'lucide-react'
import {
  lcOperationsInspectionApi,
  type LcOperationsClassGroup,
  type LcOperationsRosterItem,
  type LcOperationsSummary,
} from '@/api/lcOperationsInspection'
import type { PageResponse } from '@/types/api'
import { LcOperationsLearners } from './LcOperationsLearners'

interface QueryRegion<T> {
  data: T | undefined
  isPending: boolean
  error: unknown
}

interface LcOperationsInspectionViewProps {
  summary: QueryRegion<LcOperationsSummary>
  classGroups: QueryRegion<PageResponse<LcOperationsClassGroup>>
  includeInactive: boolean
  query: string
  sort: 'name' | 'status' | 'createdAt' | 'updatedAt'
  direction: 'asc' | 'desc'
  page: number
  lastSuccessfulAt: number | null
  refreshing: boolean
  onIncludeInactiveChange: (checked: boolean) => void
  onSearch: (query: string) => void
  onSortChange: (sort: 'name' | 'status' | 'createdAt' | 'updatedAt') => void
  onDirectionChange: (direction: 'asc' | 'desc') => void
  onPageChange: (page: number) => void
  onRefresh: () => void
  onRetrySummary: () => void
  onRetryClassGroups: () => void
  renderClassGroup: (classGroup: LcOperationsClassGroup) => React.ReactNode
  activeSection: 'class-groups' | 'learners'
  onSectionChange: (section: 'class-groups' | 'learners') => void
  learnerContent: React.ReactNode
}

function requestIdFromError(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('response' in error)) return null
  const response = error.response
  if (!response || typeof response !== 'object' || !('headers' in response)) return null
  const headers = response.headers
  if (!headers || typeof headers !== 'object') return null
  const requestId = (headers as Record<string, unknown>)['x-request-id']
  return typeof requestId === 'string' ? requestId : null
}

function RegionError({
  title,
  error,
  onRetry,
}: {
  title: string
  error: unknown
  onRetry: () => void
}) {
  const requestId = requestIdFromError(error)
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium">{title}</p>
          {requestId ? <p className="mt-1 text-xs text-red-600">requestId: {requestId}</p> : null}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-red-100"
        >
          다시 시도
        </button>
      </div>
    </div>
  )
}

const summaryCards: Array<{
  label: string
  value: (summary: LcOperationsSummary) => number
  tone: string
}> = [
  { label: '운영 중 수업반', value: (data) => data.classGroups.active, tone: 'text-emerald-700' },
  { label: '비활성 수업반', value: (data) => data.classGroups.inactive, tone: 'text-slate-700' },
  { label: '재원 원생', value: (data) => data.learners.enrolled, tone: 'text-blue-700' },
  { label: '휴원 원생', value: (data) => data.learners.onLeave, tone: 'text-amber-700' },
  { label: '대기 원생', value: (data) => data.learners.waiting, tone: 'text-slate-700' },
  { label: '퇴원 원생', value: (data) => data.learners.withdrawn, tone: 'text-slate-700' },
  { label: '데이터 확인 필요', value: (data) => data.reviewRequired, tone: 'text-red-700' },
]

export function LcOperationsInspectionView({
  summary,
  classGroups,
  includeInactive,
  query,
  sort,
  direction,
  page,
  lastSuccessfulAt,
  refreshing,
  onIncludeInactiveChange,
  onSearch,
  onSortChange,
  onDirectionChange,
  onPageChange,
  onRefresh,
  onRetrySummary,
  onRetryClassGroups,
  renderClassGroup,
  activeSection,
  onSectionChange,
  learnerContent,
}: LcOperationsInspectionViewProps) {
  const [searchText, setSearchText] = useState(query)
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">LC 운영 조회</h2>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5" />
              읽기 전용
            </span>
            {summary.data && !summary.data.organization.isActive ? (
              <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                비활성 센터
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-slate-500">센터 운영 데이터를 변경하지 않고 현재 상태를 조사합니다.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            마지막 조회: {lastSuccessfulAt ? new Date(lastSuccessfulAt).toLocaleString('ko-KR') : '—'}
          </span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>
      </div>

      {summary.isPending ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : summary.error ? (
        <RegionError title="운영 요약 조회에 실패했습니다" error={summary.error} onRetry={onRetrySummary} />
      ) : summary.data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {summaryCards.map((card) => (
            <div key={card.label} className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium text-slate-500">{card.label}</p>
              <p className={`mt-2 text-2xl font-semibold ${card.tone}`}>{card.value(summary.data!)}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex gap-2 border-b border-slate-200">
        <button type="button" onClick={() => onSectionChange('class-groups')} className={`border-b-2 px-4 py-2 text-sm font-medium ${activeSection === 'class-groups' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500'}`}>수업반</button>
        <button type="button" onClick={() => onSectionChange('learners')} className={`border-b-2 px-4 py-2 text-sm font-medium ${activeSection === 'learners' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500'}`}>원생</button>
      </div>

      {activeSection === 'class-groups' ? <section className="space-y-4" aria-labelledby="class-group-heading">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 id="class-group-heading" className="text-base font-semibold text-slate-900">수업반</h3>
            <p className="text-sm text-slate-500">담당 선생님, 일정, 정원, 휴강과 roster를 확인합니다.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <form
              onSubmit={(event) => {
                event.preventDefault()
                onSearch(searchText.trim())
              }}
              className="flex items-center gap-2"
            >
              <input
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="수업반 이름·설명 검색"
                className="w-52 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button type="submit" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
                검색
              </button>
            </form>
            <select
              value={sort}
              onChange={(event) => onSortChange(event.target.value as typeof sort)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              aria-label="수업반 정렬 기준"
            >
              <option value="name">이름</option>
              <option value="status">운영 상태</option>
              <option value="createdAt">생성일</option>
              <option value="updatedAt">변경일</option>
            </select>
            <select
              value={direction}
              onChange={(event) => onDirectionChange(event.target.value as typeof direction)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              aria-label="수업반 정렬 방향"
            >
              <option value="asc">오름차순</option>
              <option value="desc">내림차순</option>
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(event) => onIncludeInactiveChange(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              비활성 포함
            </label>
          </div>
        </div>

        {classGroups.isPending ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : classGroups.error ? (
          <RegionError title="수업반 조회에 실패했습니다" error={classGroups.error} onRetry={onRetryClassGroups} />
        ) : classGroups.data?.content.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
            조회할 수업반이 없습니다.
          </div>
        ) : (
          <>
            <div className="space-y-4">{classGroups.data?.content.map(renderClassGroup)}</div>
            {classGroups.data && classGroups.data.totalPages > 1 ? (
              <div className="flex items-center justify-center gap-3 pt-2 text-sm">
                <button
                  type="button"
                  disabled={classGroups.data.first}
                  onClick={() => onPageChange(page - 1)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40"
                >
                  이전
                </button>
                <span>{page + 1} / {classGroups.data.totalPages}</span>
                <button
                  type="button"
                  disabled={classGroups.data.last}
                  onClick={() => onPageChange(page + 1)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40"
                >
                  다음
                </button>
              </div>
            ) : null}
          </>
        )}
      </section> : learnerContent}
    </div>
  )
}

const dayLabels: Record<string, string> = {
  mon: '월',
  tue: '화',
  wed: '수',
  thu: '목',
  fri: '금',
  sat: '토',
  sun: '일',
}

function scheduleLabels(schedule: string): string[] {
  try {
    const parsed: unknown = JSON.parse(schedule)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return []
    return Object.entries(parsed).flatMap(([day, value]) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return []
      const row = value as Record<string, unknown>
      const startTime = typeof row.startTime === 'string' ? row.startTime : null
      const duration = typeof row.durationMinutes === 'number' ? row.durationMinutes : null
      if (!startTime) return []
      return [`${dayLabels[day] ?? day} ${startTime}${duration ? ` · ${duration}분` : ''}`]
    })
  } catch {
    return []
  }
}

function statusLabel(status: string): string {
  return ({
    ENROLLED: '재원',
    ON_LEAVE: '휴원',
    WAITING: '대기',
    WITHDRAWN: '퇴원',
    COMPLETED: '수강 완료',
  } as Record<string, string>)[status] ?? status
}

function RosterTable({ rows }: { rows: LcOperationsRosterItem[] }) {
  if (rows.length === 0) {
    return <p className="py-5 text-center text-sm text-slate-500">표시할 roster가 없습니다.</p>
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-[1050px] w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs font-medium text-slate-600">
          <tr>
            <th className="px-3 py-2">원생</th>
            <th className="px-3 py-2">학년</th>
            <th className="px-3 py-2">보호자 전화</th>
            <th className="px-3 py-2">상태</th>
            <th className="px-3 py-2">무료체험</th>
            <th className="px-3 py-2">대표 과정</th>
            <th className="px-3 py-2">현재 커리큘럼</th>
            <th className="px-3 py-2">진도율</th>
            <th className="px-3 py-2">미사용 레슨</th>
            <th className="px-3 py-2">수강 종료일</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.enrollmentId} className="text-slate-700">
              <td className="px-3 py-2 font-medium text-slate-900">{row.fullName}</td>
              <td className="px-3 py-2">{row.gradeLevel ?? '—'}</td>
              <td className="px-3 py-2">{row.guardianPhoneLast4 ? `••••-${row.guardianPhoneLast4}` : '—'}</td>
              <td className="px-3 py-2">{statusLabel(row.learnerStatus)}</td>
              <td className="px-3 py-2">{row.trialStatus ?? '—'}</td>
              <td className="px-3 py-2">{row.courseName ?? '—'}</td>
              <td className="px-3 py-2">{row.currentCurriculum ?? '—'}</td>
              <td className="px-3 py-2">{row.completionRate == null ? '—' : `${row.completionRate}%`}</td>
              <td className="px-3 py-2">{row.unusedLessons ?? '—'}</td>
              <td className="px-3 py-2">{row.learningEndDate ?? row.enrollmentEndDate ?? '무기한'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LcOperationsClassGroupCard({
  organizationId,
  classGroup,
}: {
  organizationId: number
  classGroup: LcOperationsClassGroup
}) {
  const [expanded, setExpanded] = useState(false)
  const [includeHistorical, setIncludeHistorical] = useState(false)
  const rosterQuery = useQuery({
    queryKey: ['admin', 'lc-operations', organizationId, 'class-groups', classGroup.timeslotId, 'roster', includeHistorical],
    queryFn: () => lcOperationsInspectionApi.getRoster(organizationId, classGroup.timeslotId, includeHistorical),
    enabled: expanded,
    staleTime: 30_000,
  })
  const schedules = scheduleLabels(classGroup.schedule)

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-5" key={classGroup.timeslotId}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-semibold text-slate-900">{classGroup.name}</h4>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${classGroup.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
              {classGroup.isActive ? '운영 중' : '비활성'}
            </span>
            {classGroup.currentlyClosed ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">현재 휴강</span>
            ) : null}
          </div>
          {classGroup.description ? <p className="mt-1 text-sm text-slate-500">{classGroup.description}</p> : null}
        </div>
        <span className="font-mono text-xs text-slate-400">timeslot #{classGroup.timeslotId}</span>
      </div>

      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" /><span>담당 {classGroup.teacherName ?? '미지정'}</span></div>
        <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" /><span>{schedules.join(', ') || '일정 미지정'}</span></div>
        <div>주 {classGroup.weeklySessionCount}회 · 정원 {classGroup.maxStudents ?? '무제한'}</div>
        <div>재원 {classGroup.currentStudents}명 · 휴원 {classGroup.onLeaveStudents}명</div>
      </div>

      <details className="mt-4 rounded-lg bg-slate-50 px-4 py-3">
        <summary className="cursor-pointer text-sm font-medium text-slate-700">휴강 이력 {classGroup.closures.length}건</summary>
        <div className="mt-3 space-y-2">
          {classGroup.closures.length === 0 ? <p className="text-sm text-slate-500">휴강 이력이 없습니다.</p> : classGroup.closures.map((closure) => (
            <div key={closure.closureId} className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-2 text-sm">
              <span>{closure.startDate} ~ {closure.endDate ?? '종료일 없음'} · {closure.reason ?? '사유 없음'}</span>
              <span className="text-xs font-medium text-slate-500">{closure.state}</span>
            </div>
          ))}
        </div>
      </details>

      <div className="mt-4 border-t border-slate-200 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            roster {expanded ? '접기' : '보기'}
          </button>
          {expanded ? (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={includeHistorical}
                onChange={(event) => setIncludeHistorical(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              과거 포함
            </label>
          ) : null}
        </div>

        {expanded ? (
          <div className="mt-3">
            {rosterQuery.isPending ? (
              <div className="flex justify-center py-5"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : rosterQuery.error ? (
              <RegionError title="roster 조회에 실패했습니다" error={rosterQuery.error} onRetry={() => void rosterQuery.refetch()} />
            ) : (
              <RosterTable rows={rosterQuery.data ?? []} />
            )}
          </div>
        ) : null}
      </div>
    </article>
  )
}

export function LcOperationsInspection({ organizationId }: { organizationId: number }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [includeInactive, setIncludeInactive] = useState(false)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<'name' | 'status' | 'createdAt' | 'updatedAt'>('name')
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const activeSection = searchParams.get('operationsSection') === 'learners' ? 'learners' : 'class-groups'
  const learnerIdParam = searchParams.get('learnerId')
  const selectedUserId = learnerIdParam && /^\d+$/.test(learnerIdParam) ? Number(learnerIdParam) : null
  const requestedDetail = searchParams.get('detail')
  const selectedDetail = requestedDetail === 'sessions' || requestedDetail === 'entitlements' || requestedDetail === 'textbooks' || requestedDetail === 'diagnostics'
    ? requestedDetail
    : 'learning'
  const summaryQuery = useQuery({
    queryKey: ['admin', 'lc-operations', organizationId, 'summary'],
    queryFn: () => lcOperationsInspectionApi.getSummary(organizationId),
    staleTime: 30_000,
  })
  const classGroupsQuery = useQuery({
    queryKey: ['admin', 'lc-operations', organizationId, 'class-groups', includeInactive, query, sort, direction, page],
    queryFn: () => lcOperationsInspectionApi.getClassGroups(organizationId, {
      includeInactive,
      query,
      sort,
      direction,
      page,
      size: 20,
    }),
    staleTime: 30_000,
  })
  const lastSuccessfulAt = Math.max(summaryQuery.dataUpdatedAt, classGroupsQuery.dataUpdatedAt) || null
  const refreshing = summaryQuery.isFetching || classGroupsQuery.isFetching

  const refreshAll = () => {
    void Promise.allSettled([summaryQuery.refetch(), classGroupsQuery.refetch()])
  }

  return (
    <LcOperationsInspectionView
      summary={summaryQuery}
      classGroups={classGroupsQuery}
      includeInactive={includeInactive}
      query={query}
      sort={sort}
      direction={direction}
      page={page}
      lastSuccessfulAt={lastSuccessfulAt}
      refreshing={refreshing}
      onIncludeInactiveChange={(checked) => {
        setIncludeInactive(checked)
        setPage(0)
      }}
      onSearch={(value) => {
        setQuery(value)
        setPage(0)
      }}
      onSortChange={(value) => {
        setSort(value)
        setPage(0)
      }}
      onDirectionChange={(value) => {
        setDirection(value)
        setPage(0)
      }}
      onPageChange={setPage}
      onRefresh={refreshAll}
      onRetrySummary={() => void summaryQuery.refetch()}
      onRetryClassGroups={() => void classGroupsQuery.refetch()}
      renderClassGroup={(classGroup) => (
        <LcOperationsClassGroupCard
          key={classGroup.timeslotId}
          organizationId={organizationId}
          classGroup={classGroup}
        />
      )}
      activeSection={activeSection}
      onSectionChange={(section) => {
        setSearchParams((current) => {
          const next = new URLSearchParams(current)
          next.set('operationsSection', section)
          if (section !== 'learners') next.delete('learnerId')
          return next
        })
      }}
      learnerContent={(
        <LcOperationsLearners
          organizationId={organizationId}
          selectedUserId={selectedUserId}
          onSelectLearner={(userId) => setSearchParams((current) => {
            const next = new URLSearchParams(current)
            next.set('operationsSection', 'learners')
            next.set('learnerId', String(userId))
            next.set('detail', 'learning')
            return next
          })}
          onBack={() => setSearchParams((current) => {
            const next = new URLSearchParams(current)
            next.delete('learnerId')
            next.delete('detail')
            return next
          })}
          selectedDetail={selectedDetail}
          onDetailChange={(detail) => setSearchParams((current) => {
            const next = new URLSearchParams(current)
            next.set('detail', detail)
            return next
          })}
        />
      )}
    />
  )
}
